<template>
	<div class="landing-slider">
		<app-slider
			pagination-align="bottom-center"
			:data="sliderData"
			:style="sliderStyle"
			v-bind="{ config }"
			class="landing-slider__slider"
			@change="onSlideChange"
		>
			<template #slide="{ data }">
				<div class="landing-slider__slide">
					<div class="landing-slider__image" :style="data.slideStyle || {}" />
				</div>
			</template>
		</app-slider>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';

import AppSlider, { SwiperProps } from '@/components/slider/AppSlider.vue';
import { LandingSliderItemModel } from '@/store/models/Slider';
import { AnyObject } from '@/tools/types';

const getUrl = (img: string) => `url(${img})`;
const sliderData: LandingSliderItemModel[] = [
	{
		id: 0,
		background: {
			main: '#404548',
		},
		slideStyle: {
			backgroundImage: getUrl(require('@images/slider/slider-img-1.png')),
		},
	},
	{
		id: 1,
		background: {
			main: '#41868E',
		},
		slideStyle: {
			backgroundImage: getUrl(require('@images/slider/slider-img-2.png')),
			backgroundPosition: 'bottom center',
		},
	},
	{
		id: 2,
		background: {
			main: '#131730',
		},
		slideStyle: {
			backgroundImage: getUrl(require('@images/slider/slider-img-3.png')),
		},
	},
	{
		id: 3,
		background: {
			main: '#656A6F',
		},
		slideStyle: {
			backgroundImage: getUrl(require('@images/slider/slider-img-4.png')),
		},
	},
];

export default defineComponent({
	components: {
		AppSlider,
	},

	emits: ['change-bg'],

	setup() {
		const config: SwiperProps = {
			loop: true,
			autoplay: {
				delay: 5000,
				disableOnInteraction: false,
			},
			speed: 700,
			allowTouchMove: false,
			grabCursor: false,
		};

		return {
			config,
			sliderData,

			activeSlideIdx: ref(0),
		};
	},

	computed: {
		activeSlide(): LandingSliderItemModel {
			return this.sliderData[this.activeSlideIdx];
		},

		sliderStyle(): AnyObject {
			return {
				backgroundColor: this.activeSlide.background.main,
			};
		},
	},

	methods: {
		onSlideChange(index: number) {
			this.activeSlideIdx = index;

			const bg = this.activeSlide.background;

			this.$emit('change-bg', bg.side ?? bg.main);
		},
	},
});
</script>

<style lang="scss">
.landing-slider {
	--active-color: #{$color-white};

	$self: &;

	.slider,
	.swiper,
	.swiper-slide {
		height: 100%;
	}

	.swiper {
		.swiper-pagination {
			&--align-bottom-center {
				bottom: 3.2rem;

				@include max-lg-and-portrait {
					bottom: 3rem;
				}
			}
		}

		.swiper-pagination-bullet {
			border: 1px solid var(--active-color);

			&-active {
				background-color: var(--active-color);
			}
		}
	}

	&__slider {
		transition: background-color $landing-bg-transition;
		border-radius: 1.6rem 0 0 1.6rem;

		@include max-md-and-portrait {
			border-radius: 1.6rem 1.6rem 0 0;
		}
	}

	&__slide {
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		width: 100%;
		height: 100%;
		position: relative;
		box-sizing: border-box;
		background-repeat: no-repeat;
		background-position: center;
		background-size: contain;
	}

	&__image {
		width: 100%;
		height: 100%;
		position: absolute;
		z-index: 0;
		background-repeat: no-repeat;
		background-position: center;
		background-size: contain;
	}

	&__title {
		@include heading-3;
		margin-bottom: 4.4rem;
		padding-bottom: 3.2rem;
		z-index: 1;
		color: var(--active-color);
		text-align: center;
	}
}
</style>
